/**
 * CR555: Per-step AUTO/MANUAL mode toggle for S1 sub-steps
 * Calls rpc_cr075_set_step_mode to toggle mode with smart enqueue/dequeue
 */

import { useState, useCallback } from "react";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface StepModeToggleProps {
  batchRunId: string;
  stepKey: string;
  currentMode: string | null;
  currentStatus: string | null;
  onModeChanged: (newMode: string, newStatus: string | null) => void;
  disabled?: boolean;
  /** CRxxx: Reason why toggle is locked/disabled (e.g. "Global S1 Auto-Run enabled") */
  lockReason?: string | null;
}

export function StepModeToggle({
  batchRunId,
  stepKey,
  currentMode,
  currentStatus,
  onModeChanged,
  disabled = false,
  lockReason = null,
}: StepModeToggleProps) {
  const { toast } = useToast();
  const [toggling, setToggling] = useState(false);

  const isAuto = (currentMode || "MANUAL").toUpperCase() === "AUTO";
  const isRunning = currentStatus === "RUNNING" || currentStatus === "IN_PROGRESS";
  const isTerminal = ["COMPLETE", "PARTIAL", "FAILED", "SKIPPED"].includes(currentStatus || "");
  const canToggle = !disabled && !toggling && !isRunning;

  const handleToggle = useCallback(async (checked: boolean) => {
    const newMode = checked ? "AUTO" : "MANUAL";
    setToggling(true);
    try {
      const { data, error } = await supabase.rpc("rpc_cr075_set_step_mode", {
        p_batch_run_id: batchRunId,
        p_step_key: stepKey,
        p_mode: newMode,
      });

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        return;
      }

      const result = data as { success: boolean; action?: string; new_mode?: string; new_status?: string; message?: string; error?: string };
      if (!result?.success) {
        // UX hardening: show friendly message instead of raw "Step not found"
        const msg = result?.error === "Step not found"
          ? `${stepKey} isn't configured for auto-run in this batch run yet.`
          : (result?.error || "Failed to set mode");
        toast({ title: "Error", description: msg, variant: "destructive" });
        return;
      }

      onModeChanged(result.new_mode || newMode, result.new_status || null);

      // Show feedback for interesting actions
      if (result.action === "enqueued") {
        toast({ title: "Step queued", description: `${stepKey} will run automatically` });
      } else if (result.action === "dequeued") {
        toast({ title: "Step paused", description: `${stepKey} switched to manual mode` });
      }
    } catch (err) {
      toast({ title: "Error", description: String(err), variant: "destructive" });
    } finally {
      setToggling(false);
    }
  }, [batchRunId, stepKey, onModeChanged, toast]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          {toggling ? (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          ) : (
            <Switch
              checked={isAuto}
              onCheckedChange={handleToggle}
              disabled={!canToggle}
              className="h-4 w-7 data-[state=checked]:bg-primary [&>span]:h-3 [&>span]:w-3"
            />
          )}
          <span className={`text-[10px] font-medium ${isAuto ? "text-primary" : "text-muted-foreground"}`}>
            {isAuto ? "AUTO" : "MANUAL"}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs max-w-[200px]">
        {lockReason
          ? lockReason
          : isRunning
            ? "Cannot change mode while step is running"
            : isTerminal
              ? (isAuto ? "AUTO is set for the next attempt." : "MANUAL is set for the next attempt.")
              : isAuto
                ? "Auto-run when ready — step chains automatically"
                : "Paused — click Run to start manually"
        }
      </TooltipContent>
    </Tooltip>
  );
}
